/**
 * Suggestions hooks — Tasks-side surface of the platform Suggestions API
 * shipped in F1.4. Filters down to `targetType='task'` for the inbox view.
 *
 * Accept-on-task semantics: when a user accepts a task-targeting
 * suggestion, the platform applies the patch server-side via the shared
 * `applyTaskPatch` helper and emits a `decisions` row with
 * `from_suggestion_id` set. The hook just invalidates the task / list
 * queries so the UI reflects the new state.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiSend } from './client';

export type SuggestionStatus = 'pending' | 'accepted' | 'rejected' | 'expired';
export type SuggestionConfidence = 'low' | 'medium' | 'high' | null;

export interface SuggestionDto {
  id: string;
  agentId: string;
  kind: string;
  targetType: string;
  targetId: string;
  proposed: Record<string, unknown>;
  rationale: string;
  confidence: SuggestionConfidence;
  status: SuggestionStatus;
  createdAt: string;
  resolvedAt: string | null;
  resolvedBy: string | null;
  expiresAt: string | null;
}

export interface SuggestionsListResponse {
  suggestions: SuggestionDto[];
}

export function useTaskSuggestions(status: SuggestionStatus = 'pending') {
  return useQuery({
    queryKey: ['suggestions', { targetType: 'task', status }],
    queryFn: () =>
      apiGet<SuggestionsListResponse>(
        `/platform/suggestions?targetType=task&status=${encodeURIComponent(status)}`,
      ),
    staleTime: 15_000,
  });
}

export function useAcceptSuggestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiSend<{ suggestion: SuggestionDto; decision: { id: string; createdAt: string } }>(
        'POST',
        `/platform/suggestions/${id}/accept`,
        {},
      ),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['suggestions'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['task'] });
      qc.invalidateQueries({ queryKey: ['inbox'] });
      void id;
    },
  });
}

export function useRejectSuggestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiSend<{ suggestion: SuggestionDto }>(
        'POST',
        `/platform/suggestions/${id}/reject`,
        {},
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['suggestions'] });
    },
  });
}
